/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useLocation, useHistory } from "react-router-dom";

// @mui material components
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";

// Custom styles for the Configurator
import ConfiguratorRoot from "examples/Configurator/ConfiguratorRoot";

// Vision UI Dashboard React context
import {
  useVisionUIController,
  setOpenConfigurator,
} from "context";
import { TC } from "../../tokens/text";

// ── Page-specific help content ──────────────────────────────────────────────────

const README_BASE = "https://gitlab.com/elboards/ledger-hub/-/blob/main/README.md";

const PAGE_HELP = [
  {
    match: "/dashboard",
    pageTitle: "Dashboard",
    description: "Your practice overview — key metrics, recent activity, and quick-access cards for each module.",
    tips: [
      "Use the summary cards to jump directly to areas needing attention.",
      "Recent notifications appear in the top-right bell icon.",
      "Switch tenants via the organisation selector in the sidenav.",
    ],
    readmeAnchor: "#1-multi-tenant-architecture",
  },
  {
    match: "/client-profiles",
    pageTitle: "Client Profiles",
    description: "Manage all your business clients — Companies House data, contact details, VAT and tax identifiers.",
    tips: [
      "Click a client row to open the full profile detail view.",
      "Use the search bar to find clients by name or company number.",
      "Companies House data is fetched automatically on profile creation.",
      "Assign services to a client from the Services tab — each assignment immediately auto-creates a job and assigns it to the default staff member set in Settings → Service Defaults.",
    ],
    readmeAnchor: "#2-client-profile-management",
  },
  {
    match: "/directors",
    pageTitle: "Directors",
    description: "View and manage director records across all client companies, synced from Companies House.",
    tips: [
      "Filter by client to see directors for a specific company.",
      "Director appointments and resignations are tracked automatically.",
      "Alerts appear when a director change is detected via CH polling.",
    ],
    readmeAnchor: "#12-directors-management",
  },
  {
    match: "/individual-clients",
    pageTitle: "Individual Clients",
    description: "Manage personal tax clients (self-assessment, sole traders) separately from business entities.",
    tips: [
      "Use the detail view to record UTR, NI number and tax year data.",
      "Individual clients do not have Companies House records.",
      "Go to the Services tab on any client to assign a service — this automatically creates a job for that client and assigns it to the default staff member you configured in Settings.",
      "Once a service is assigned, future recurring jobs (e.g. next year's Self Assessment) are created automatically every night by the scheduler — no manual action needed.",
    ],
    readmeAnchor: "#14-individual-client-management",
  },
  {
    match: "/jobs-board",
    pageTitle: "Jobs Board",
    description: "All client service jobs in one place — automatically created and assigned as soon as a service is added to a client.",
    tips: [
      "Jobs are created automatically in two ways: (1) immediately when you assign a service to a client, and (2) nightly by the scheduler which generates recurring jobs up to 1 year ahead for every active service assignment.",
      "Each job is automatically assigned to the default staff member you configure per service type in Settings → Service Defaults. No manual assignment needed.",
      "The nightly scheduler runs across all clients and tenants — you never need to manually create recurring jobs (e.g. monthly bookkeeping, annual accounts, quarterly VAT returns).",
      "Filter by client, assignee, status, or due date using the row above the table.",
    ],
    readmeAnchor: "#16-jobs-board",
  },
  {
    match: "/tasks",
    pageTitle: "Filing Tasks",
    description: "Track statutory deadlines — Corporation Tax, Annual Accounts, and Confirmation Statements.",
    tips: [
      "Deadlines are auto-calculated based on client year-end dates.",
      "Overdue items are highlighted in red.",
      "Use the export button to download a deadline schedule.",
    ],
    readmeAnchor: "#11-tasks",
  },
  {
    match: "/period-close",
    pageTitle: "Period Close",
    description: "Lock accounting periods after readiness checks — VAT, bank recs, and payroll sign-off.",
    tips: [
      "All readiness checks must be green before a period can be locked.",
      "Locked periods prevent further transaction posting for that month.",
      "Use filters to view period close status across all clients.",
    ],
    readmeAnchor: "#6-vat--mtd",
  },
  {
    match: "/vat-return",
    pageTitle: "VAT Return",
    description: "Prepare and submit VAT returns under Making Tax Digital (MTD) directly to HMRC.",
    tips: [
      "Connect to HMRC via the MTD authorisation flow before submitting.",
      "Review the calculation summary before finalising each return.",
      "Submitted returns are locked and stored for audit purposes.",
    ],
    readmeAnchor: "#6-vat--mtd",
  },
  {
    match: "/exceptions",
    pageTitle: "Exceptions",
    description: "Review flagged transactions and reconciliation mismatches that require attention.",
    tips: [
      "Resolve exceptions before closing a period.",
      "Use the filter to prioritise by client or exception type.",
      "Exceptions auto-clear once the underlying transaction is corrected.",
    ],
    readmeAnchor: "#8-reports",
  },
  {
    match: "/reports",
    pageTitle: "Reports",
    description: "Generate practice-wide and client-specific reports for management and compliance.",
    tips: [
      "Reports can be exported to CSV or PDF.",
      "Use date range filters to scope reports to specific periods.",
      "Saved report configurations can be reused for recurring reports.",
    ],
    readmeAnchor: "#8-reports",
  },
  {
    match: "/advisory",
    pageTitle: "Advisory",
    description: "Track advisory engagements, record client decisions, and manage fee proposals.",
    tips: [
      "Link advisory work to specific client profiles.",
      "Use notes to document key decisions and outcomes.",
    ],
    readmeAnchor: "#7-advisory-module",
  },
  {
    match: "/bank-feeds",
    pageTitle: "Bank Feeds",
    description: "Connect client bank accounts via Open Banking to feed transactions automatically.",
    tips: [
      "Consents expire every 90 days — renew before expiry to avoid breaks.",
      "Check the Consent Audit Log to review connection history.",
      "Each connection is scoped to a specific client profile.",
    ],
    readmeAnchor: "#5-bank-feeds--open-banking",
  },
  {
    match: "/consent-audit-log",
    pageTitle: "Consent Audit Log",
    description: "Full history of all Open Banking consent grants, renewals, and revocations.",
    tips: [
      "Useful for compliance — all consent events are timestamped.",
      "Filter by client or date range to find specific consent history.",
    ],
    readmeAnchor: "#5-bank-feeds--open-banking",
  },
  {
    match: "/email-inbox",
    pageTitle: "Email Inbox",
    description: "View and manage inbound emails linked to your practice mailbox.",
    tips: [
      "Emails can be linked to client profiles for record-keeping.",
      "Use filters to find emails by client or date.",
    ],
    readmeAnchor: "#10-email-integration",
  },
  {
    match: "/email-config",
    pageTitle: "Email Configuration",
    description: "Configure inbound and outbound email integration for your practice.",
    tips: [
      "IMAP credentials are required for inbox access.",
      "Test the connection before saving to confirm settings are correct.",
    ],
    readmeAnchor: "#10-email-integration",
  },
  {
    match: "/tenant-users",
    pageTitle: "User Management",
    description: "Manage staff users for your practice — invite, suspend, or deactivate accounts.",
    tips: [
      "Assign roles when inviting new users to control their permissions.",
      "Deactivated users cannot sign in but their data is retained.",
    ],
    readmeAnchor: "#13-authentication--users",
  },
  {
    match: "/role-assignment",
    pageTitle: "Role Management",
    description: "Define and assign roles that control what each user can see and do.",
    tips: [
      "Roles are additive — a user can hold multiple roles.",
      "Changes to role permissions take effect immediately on next page load.",
    ],
    readmeAnchor: "#13-authentication--users",
  },
  {
    match: "/organisations",
    pageTitle: "Organisations",
    description: "Manage practice tenants and their configuration (Super Admin only).",
    tips: [
      "Each organisation has its own isolated data and user base.",
      "Switching organisations updates all data views across the app.",
    ],
    readmeAnchor: "#1-multi-tenant-architecture",
  },
  {
    match: "/notifications",
    pageTitle: "Notifications",
    description: "Review all system and activity notifications for your practice.",
    tips: [
      "Mark notifications as read to keep your inbox clean.",
      "Click a notification to navigate directly to the related item.",
    ],
    readmeAnchor: "#1-multi-tenant-architecture",
  },
  {
    match: "/settings",
    pageTitle: "Settings",
    description: "Manage your workspace preferences and configure automatic job assignment across your practice.",
    tips: [
      "Service Defaults (bottom of this page) — set a default staff member per service type (e.g. 'VAT Return → Jane'). Every time that service is assigned to any client, the job is automatically assigned to that person.",
      "Default assignees flow through to all auto-generated jobs: the first job created when a service is assigned, and every future recurring job created by the nightly scheduler.",
      "You can always override the assignee when assigning a service to a specific client — the default is just a starting point.",
      "Display, Accounting and Behaviour settings apply instantly — no save button needed.",
    ],
    readmeAnchor: "#1-multi-tenant-architecture",
  },
];

const DEFAULT_HELP = {
  pageTitle: "LedgerHub",
  description: "UK Accounting Practice Platform — manage clients, filings, VAT, bank feeds, and more from one dashboard.",
  tips: [
    "Use the sidenav to navigate between modules.",
    "The search bar in the top navbar lets you find clients quickly.",
    "Click the gear icon on any page for contextual guidance.",
  ],
  readmeAnchor: "#ledgerhub--uk-accounting-practice-platform",
};

function getHelp(pathname) {
  for (const entry of PAGE_HELP) {
    if (pathname === entry.match || pathname.startsWith(entry.match + "/")) {
      return entry;
    }
  }
  return DEFAULT_HELP;
}

// ── Component ────────────────────────────────────────────────────────────────────

function Configurator() {
  const [controller, dispatch] = useVisionUIController();
  const { openConfigurator } = controller;
  const location = useLocation();
  const history = useHistory();

  const help = getHelp(location.pathname);
  const isOnSettings = location.pathname === "/settings";

  const handleClose = () => setOpenConfigurator(dispatch, false);

  const handleGoToSettings = () => {
    setOpenConfigurator(dispatch, false);
    history.push("/settings");
  };

  return (
    <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }}>
      {/* Header */}
      <VuiBox
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        pt={3}
        pb={0.8}
        px={3}
        sx={{ minWidth: 0 }}
      >
        <VuiBox sx={{ minWidth: 0, flex: 1, mr: 1 }}>
          <VuiTypography color="text" variant="h6" fontWeight="bold">
            Application Guidance
          </VuiTypography>
          <VuiTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
            Help for this page
          </VuiTypography>
        </VuiBox>
        <Icon
          sx={({ typography: { size, fontWeightBold }, palette: { white } }) => ({
            fontSize: `${size.md} !important`,
            fontWeight: `${fontWeightBold} !important`,
            stroke: `${white.main} !important`,
            strokeWidth: "2px",
            cursor: "pointer",
            mt: 0.5,
          })}
          onClick={handleClose}
        >
          close
        </Icon>
      </VuiBox>

      <Divider light />

      <VuiBox pt={1} pb={3} px={3} sx={{ width: "100%", boxSizing: "border-box", minWidth: 0 }}>

        {/* Page title */}
        <VuiTypography
          variant="caption"
          color="text"
          sx={{ opacity: 0.45, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "10px" }}
        >
          Current page
        </VuiTypography>
        <VuiTypography color="text" variant="h6" fontWeight="bold" mt={0.5} mb={1}>
          {help.pageTitle}
        </VuiTypography>

        {/* Description */}
        <VuiTypography
          component="p"
          sx={{ color: TC.midtone, fontSize: "13px", lineHeight: 1.7, mt: 0, mb: 0, wordBreak: "break-word", whiteSpace: "normal" }}
        >
          {help.description}
        </VuiTypography>

        <Divider light sx={{ my: 2 }} />

        {/* Tips */}
        <VuiTypography
          variant="caption"
          color="text"
          sx={{ opacity: 0.45, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "10px" }}
        >
          Tips
        </VuiTypography>
        <VuiBox mt={1.5} display="flex" flexDirection="column" gap={1.5}>
          {help.tips.map((tip, i) => (
            <VuiBox key={i} display="flex" gap={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
              <VuiBox
                sx={{
                  minWidth: 6, height: 6, borderRadius: "50%",
                  background: "rgba(0,117,255,0.9)",
                  mt: "6px", flexShrink: 0,
                }}
              />
              <VuiTypography
                component="p"
                sx={{ color: TC.primary, fontSize: "13px", lineHeight: 1.7, flex: 1, minWidth: 0, mt: 0, mb: 0, wordBreak: "break-word", whiteSpace: "normal" }}
              >
                {tip}
              </VuiTypography>
            </VuiBox>
          ))}
        </VuiBox>

        <Divider light sx={{ my: 2 }} />

        {/* Documentation link */}
        <Link
          href={`${README_BASE}${help.readmeAnchor}`}
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
        >
          <VuiBox
            display="flex"
            alignItems="center"
            gap={0.8}
            sx={{
              p: 1.5,
              borderRadius: "10px",
              border: "1px solid rgba(0,117,255,0.3)",
              background: "rgba(0,117,255,0.07)",
              cursor: "pointer",
              "&:hover": { background: "rgba(0,117,255,0.15)", borderColor: "rgba(0,117,255,0.6)" },
              transition: "all 0.2s",
            }}
          >
            <Icon sx={{ color: "rgba(0,117,255,0.9)", fontSize: "18px !important" }}>menu_book</Icon>
            <VuiTypography variant="caption" sx={{ color: "rgba(0,117,255,0.9)", fontSize: "12px", fontWeight: 600 }}>
              Open documentation →
            </VuiTypography>
          </VuiBox>
        </Link>

        {/* Settings shortcut — hidden when already on /settings */}
        {!isOnSettings && (
          <VuiBox mt={1.5}>
            <VuiButton
              color="info"
              variant="outlined"
              size="small"
              fullWidth
              onClick={handleGoToSettings}
              sx={{ fontSize: "12px" }}
            >
              ⚙️ &nbsp;Go to Settings
            </VuiButton>
          </VuiBox>
        )}

      </VuiBox>
    </ConfiguratorRoot>
  );
}

export default Configurator;


